/**
 * 🔗 SERVICE REQUESTS - PONT VERS FIREBASE API
 * 
 * Ce fichier fait le lien entre l'interface attendue par WaitingListView
 * et notre système Firebase API.
 */

import { 
  watchOpenRequests as watchFromApi, 
  watchAssignedRequests as watchAcceptedFromApi, 
  cancelDriverHelp as cancelDriverHelpApi,
  deleteRequest as deleteRequestApi,
  updateRequest as updateRequestApi,
} from './firebaseApi';

console.log('🔗 [requests] Service requests chargé');

/**
 * Écouter les demandes ouvertes (en attente)
 * Signature attendue par WaitingListView: watchOpenRequests(meetingAtFilter, callback)
 */
export function watchOpenRequests(meetingAtFilter, cb) {
  console.log('🔍 [requests] watchOpenRequests appelé avec:', { meetingAtFilter, cbType: typeof cb });
  
  if (typeof cb !== 'function') {
    console.error('❌ [requests] Callback invalide dans watchOpenRequests');
    return () => {};
  }

  try {
    const unsubscribe = watchFromApi({
      meetingAtFilter: typeof meetingAtFilter === 'number' ? meetingAtFilter : undefined,
      onData: (rows) => {
        console.log('🔍 [requests] watchOpenRequests - Données reçues:', rows?.length || 0);
        
        try {
          if (!Array.isArray(rows)) {
            console.warn('⚠️ [requests] Données non-array reçues:', rows);
            cb([]);
            return;
          }

          // Filtrer les demandes en attente
          // Inclure les demandes qui ont encore des places restantes (même si déjà partiellement remplies)
          const filtered = rows
            .filter(r => {
              const seatsAssigned = Number(r.seatsAssigned || 0);
              const seatsNeeded = Number(r.seatsNeeded || 1);
              const seatsRemaining = Number(
                typeof r.seatsRemaining === 'number'
                  ? r.seatsRemaining
                  : Math.max(0, seatsNeeded - seatsAssigned)
              );
              
              // Inclure si il reste des places (même si déjà partiellement remplies)
              const hasRemainingSeats = seatsRemaining > 0;
              
              console.log(`🔍 [requests] ${r.id}: assigned=${r.assigned}, seatsRemaining=${seatsRemaining}, hasRemainingSeats=${hasRemainingSeats}`);
              return hasRemainingSeats;
            })
            .filter(r => r.meetingAt >= Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 jours dans le passé
          
          console.log('🔍 [requests] watchOpenRequests - Après filtrage:', filtered.length);
          
          // Trier par date puis par nom
          filtered.sort((a, b) => 
            (a.meetingAt - b.meetingAt) || 
            String(a.name || a.passengerName).localeCompare(String(b.name || b.passengerName))
          );
          
          cb(filtered);
        } catch (error) {
          console.error('❌ [requests] Erreur traitement watchOpenRequests:', error);
          cb([]);
        }
      }
    });

    console.log('✅ [requests] Listener watchOpenRequests installé');
    return () => {
      console.log('🧹 [requests] Nettoyage watchOpenRequests');
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  } catch (error) {
    console.error('❌ [requests] Erreur setup watchOpenRequests:', error);
    return () => {};
  }
}

/**
 * Écouter les demandes acceptées
 * Signatures supportées: 
 * - watchAcceptedRequests(meetingAtFilter, callback)
 * - watchAcceptedRequests(callback, errorCallback)
 */
export function watchAcceptedRequests(param1, param2) {
  console.log('✅ [requests] watchAcceptedRequests appelé avec:', { param1: typeof param1, param2: typeof param2 });
  
  let meetingAtFilter, cb;
  
  // Détecter la signature utilisée
  if (typeof param1 === 'function') {
    // Signature: watchAcceptedRequests(callback, errorCallback)
    meetingAtFilter = null;
    cb = param1;
    console.log('✅ [requests] Signature détectée: (callback, errorCallback)');
  } else {
    // Signature: watchAcceptedRequests(meetingAtFilter, callback)
    meetingAtFilter = param1;
    cb = param2;
    console.log('✅ [requests] Signature détectée: (meetingAtFilter, callback)');
  }
  
  if (typeof cb !== 'function') {
    console.error('❌ [requests] Callback invalide dans watchAcceptedRequests');
    return () => {};
  }

  try {
    const unsubscribe = watchAcceptedFromApi({
      meetingAtFilter: typeof meetingAtFilter === 'number' ? meetingAtFilter : undefined,
      onData: (rows) => {
        console.log('✅ [requests] watchAcceptedRequests - Données reçues:', rows?.length || 0);
        
        try {
          if (!Array.isArray(rows)) {
            console.warn('⚠️ [requests] Données non-array reçues:', rows);
            cb([]);
            return;
          }

          // Le filtrage d'expiration est déjà fait dans firebaseApi.js
          // On ne filtre plus ici pour éviter de réintroduire des demandes expirées
          const filtered = rows.filter(r => r.assigned === true);
          
          console.log('✅ [requests] watchAcceptedRequests - Après filtrage:', filtered.length);
          
          // Trier par date puis par nom
          filtered.sort((a, b) => 
            (a.meetingAt - b.meetingAt) || 
            String(a.name || a.passengerName).localeCompare(String(b.name || b.passengerName))
          );
          
          cb(filtered);
        } catch (error) {
          console.error('❌ [requests] Erreur traitement watchAcceptedRequests:', error);
          cb([]);
        }
      }
    });

    console.log('✅ [requests] Listener watchAcceptedRequests installé');
    return () => {
      console.log('🧹 [requests] Nettoyage watchAcceptedRequests');
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  } catch (error) {
    console.error('❌ [requests] Erreur setup watchAcceptedRequests:', error);
    return () => {};
  }
}

/**
 * Fonctions pour les compteurs (sans filtrage par date)
 */
export function watchAllRequestsForCounts(param1, param2) {
  console.log('📊 [requests] watchAllRequestsForCounts appelé avec:', { param1: typeof param1, param2: typeof param2 });
  
  // Détecter la signature : si param1 est une fonction, c'est le callback
  let cb = typeof param1 === 'function' ? param1 : param2;
  
  if (typeof cb !== 'function') {
    console.error('❌ [requests] Callback invalide dans watchAllRequestsForCounts');
    return () => {};
  }

  try {
    const unsubscribe = watchFromApi({
      meetingAtFilter: undefined, // Pas de filtre par date
      onData: (rows) => {
        console.log('📊 [requests] watchAllRequestsForCounts - Toutes les demandes:', rows?.length || 0);
        
        try {
          if (!Array.isArray(rows)) {
            cb([]);
            return;
          }

          // Pour les compteurs, on veut TOUTES les demandes en attente, sans filtre de date
          // Inclure les demandes qui ont encore des places restantes (même si partiellement remplies)
          const filtered = rows.filter(r => {
            const isAssigned = r.assigned === true;
            const seatsAssigned = Number(r.seatsAssigned || 0);
            const seatsNeeded = Number(r.seatsNeeded || 1);
            const seatsRemaining = Number(
              typeof r.seatsRemaining === 'number'
                ? r.seatsRemaining
                : Math.max(0, seatsNeeded - seatsAssigned)
            );
            
            // Inclure si pas complètement assignée OU s'il reste des places
            const hasRemainingSeats = seatsRemaining > 0;
            const shouldInclude = !isAssigned || hasRemainingSeats;
            
            if (shouldInclude) {
              console.log(`📊 [requests] watchAllRequestsForCounts - Demande incluse: ${r.id} (${r.name || r.passengerName}), assigned=${isAssigned}, seatsRemaining=${seatsRemaining}`);
            }
            
            return shouldInclude;
          });
          console.log('📊 [requests] watchAllRequestsForCounts - Après filtrage (sans date):', filtered.length);
          cb(filtered);
        } catch (error) {
          console.error('❌ [requests] Erreur traitement watchAllRequestsForCounts:', error);
          cb([]);
        }
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  } catch (error) {
    console.error('❌ [requests] Erreur setup watchAllRequestsForCounts:', error);
    return () => {};
  }
}

export function watchAllAcceptedForCounts(param1, param2) {
  console.log('📊 [requests] watchAllAcceptedForCounts appelé avec:', { param1: typeof param1, param2: typeof param2 });
  
  // Détecter la signature : si param1 est une fonction, c'est le callback
  let cb = typeof param1 === 'function' ? param1 : param2;
  
  if (typeof cb !== 'function') {
    console.error('❌ [requests] Callback invalide dans watchAllAcceptedForCounts');
    return () => {};
  }

  try {
    const unsubscribe = watchAcceptedFromApi({
      meetingAtFilter: undefined, // Pas de filtre par date
      onData: (rows) => {
        console.log('📊 [requests] watchAllAcceptedForCounts - Toutes les acceptées:', rows?.length || 0);
        
        try {
          if (!Array.isArray(rows)) {
            cb([]);
            return;
          }

          // Pour les compteurs, on veut TOUTES les demandes acceptées, sans filtre de date
          // Inclure les demandes assignées ET les demandes partiellement remplies (avec au moins un conducteur)
          const filtered = rows.filter(r => {
            const isAssigned = r.assigned === true;
            const seatsAssigned = Number(r.seatsAssigned || 0);
            const hasDrivers = r.drivers && Object.keys(r.drivers).length > 0;
            const hasSeatsAssigned = seatsAssigned > 0;
            
            const shouldInclude = isAssigned || (hasDrivers && hasSeatsAssigned);
            
            if (shouldInclude && hasDrivers) {
              const driverNames = Object.keys(r.drivers);
              console.log(`📊 [requests] watchAllAcceptedForCounts - Demande incluse: ${r.id} (${r.name || r.passengerName}), drivers: [${driverNames.join(', ')}], seats: ${seatsAssigned}/${r.seatsNeeded || 1}`);
            }
            
            return shouldInclude;
          });
          console.log('📊 [requests] watchAllAcceptedForCounts - Après filtrage (sans date):', filtered.length);
          cb(filtered);
        } catch (error) {
          console.error('❌ [requests] Erreur traitement watchAllAcceptedForCounts:', error);
          cb([]);
        }
      }
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  } catch (error) {
    console.error('❌ [requests] Erreur setup watchAllAcceptedForCounts:', error);
    return () => {};
  }
}

/**
 * Supprimer une demande
 */
export async function deleteRequest(id) {
  console.log('🗑️ [requests] deleteRequest appelé pour:', id);
  
  try {
    const result = await deleteRequestApi(id);
    console.log('✅ [requests] Demande supprimée avec succès:', id);
    return result;
  } catch (error) {
    console.error('❌ [requests] Erreur lors de la suppression de la demande:', error);
    throw error;
  }
}

/**
 * Modifier une demande
 */
export async function updateRequest(id, payload) {
  console.log("✏️ [requests] updateRequest appelé pour:", id, payload);

  try {
    const result = await updateRequestApi(id, payload);
    if (!result?.ok) {
      throw new Error(result?.error || "Échec de la mise à jour");
    }
    return result;
  } catch (error) {
    console.error("❌ [requests] Erreur lors de la mise à jour:", error);
    throw error;
  }
}

/**
 * Annuler l'aide d'un conducteur
 */
export async function cancelDriverHelp(params) {
  console.log('🚫 [requests] cancelDriverHelp appelé avec:', params);
  
  try {
    const result = await cancelDriverHelpApi(params);
    
    if (result && result.ok) {
      console.log('✅ [requests] Aide du conducteur annulée avec succès:', result);
    } else {
      console.error('❌ [requests] Échec de l\'annulation:', result);
    }
    
    return result;
  } catch (error) {
    console.error('❌ [requests] Erreur lors de l\'annulation de l\'aide:', error);
    throw error;
  }
}

console.log('✅ [requests] Service requests initialisé avec succès');